import React, { useMemo, useState } from 'react';
import {
    Alert,
    Badge,
    Button,
    Group,
    Modal,
    Select,
    Stack,
    Table,
    Text,
} from '@mantine/core';
import { CalendarDateInput } from '../../../global/component/molecules/CalendarDateInput';
import { useTranslation } from 'react-i18next';
import { IconAlertTriangle } from '@tabler/icons-react';
import {
    IScenarioApplySummary,
    IScenarioDetail,
} from '../../../global/entities/Scenario/Scenario.ts';
import {
    useApplyScenario,
    useSearchPatientUsers,
} from '../../../global/entities/Scenario/queries/useScenarios.ts';

interface IProps {
    scenario: IScenarioDetail;
    opened: boolean;
    onClose: () => void;
}

interface IUserOption {
    value: string;
    label: string;
}

const ApplyScenarioModal: React.FC<IProps> = ({ scenario, opened, onClose }) => {
    const { t } = useTranslation();
    const [startDate, setStartDate] = useState<Date | null>(new Date());
    const [mappings, setMappings] = useState<Record<number, number | null>>({});
    const [search, setSearch] = useState('');
    // Per-slot stash of the picked user's option so the Select can keep
    // displaying its label even when the active search no longer returns it.
    const [pickedOptions, setPickedOptions] = useState<Record<number, IUserOption>>({});
    const [result, setResult] = useState<IScenarioApplySummary | null>(null);

    const apply = useApplyScenario(scenario.id);
    const { data: users, isLoading: usersLoading } = useSearchPatientUsers(search);

    const userOptions = useMemo<IUserOption[]>(
        () =>
            (users ?? []).map((u) => ({
                value: u.userID.toString(),
                label: `${u.firstName} ${u.lastName} (${u.email})`,
            })),
        [users],
    );

    const dataForSlot = (slotId: number): IUserOption[] => {
        const picked = pickedOptions[slotId];
        if (!picked) return userOptions;
        if (userOptions.some((o) => o.value === picked.value)) return userOptions;
        return [picked, ...userOptions];
    };

    const allMapped = scenario.slots.every((s) => mappings[s.id]);

    const handleApply = async () => {
        if (!startDate || !allMapped) return;
        const summary = await apply.mutateAsync({
            startDate: startDate.toISOString(),
            mappings: scenario.slots.map((s) => ({ slotID: s.id, userID: mappings[s.id]! })),
        });
        setResult(summary);
    };

    const handleClose = () => {
        setResult(null);
        setMappings({});
        setPickedOptions({});
        setStartDate(new Date());
        onClose();
    };

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title={t('scenario.applyTitle')}
            size="lg"
        >
            {!result && (
                <Stack>
                    <Alert color="orange" icon={<IconAlertTriangle />}>
                        {t('scenario.applyWarning')}
                    </Alert>
                    <CalendarDateInput
                        label={t('scenario.applyStartDate')}
                        value={startDate}
                        onChange={(v: any) => setStartDate(v ? new Date(v) : null)}
                    />
                    <Text fw={500}>{t('scenario.applyAssignSlots')}</Text>
                    <Table>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>{t('scenario.slotLabel')}</Table.Th>
                                <Table.Th>{t('scenario.applyUser')}</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {scenario.slots.map((slot) => (
                                <Table.Tr key={slot.id}>
                                    <Table.Td>{slot.label}</Table.Td>
                                    <Table.Td>
                                        <Select
                                            placeholder={t('scenario.applyUserSearch')}
                                            data={dataForSlot(slot.id)}
                                            searchable
                                            onSearchChange={setSearch}
                                            value={mappings[slot.id]?.toString() ?? null}
                                            onChange={(val) => {
                                                setMappings((prev) => ({
                                                    ...prev,
                                                    [slot.id]: val ? parseInt(val) : null,
                                                }));
                                                if (!val) {
                                                    setPickedOptions((prev) => {
                                                        const next = { ...prev };
                                                        delete next[slot.id];
                                                        return next;
                                                    });
                                                    return;
                                                }
                                                const opt =
                                                    userOptions.find((o) => o.value === val) ??
                                                    pickedOptions[slot.id];
                                                if (opt)
                                                    {setPickedOptions((prev) => ({
                                                        ...prev,
                                                        [slot.id]: opt,
                                                    }));}
                                            }}
                                            nothingFoundMessage={
                                                usersLoading
                                                    ? t('common.loading')
                                                    : search.length < 2
                                                        ? t('scenario.applyTypeToSearch')
                                                        : t('scenario.noResults')
                                            }
                                        />
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                    <Group justify="flex-end">
                        <Button variant="outline" onClick={handleClose}>
                            {t('button.cancel')}
                        </Button>
                        <Button
                            color="red"
                            disabled={!startDate || !allMapped}
                            loading={apply.isPending}
                            onClick={handleApply}
                        >
                            {t('scenario.applyConfirm')}
                        </Button>
                    </Group>
                </Stack>
            )}

            {result && (
                <Stack>
                    <Text fw={500}>{t('scenario.applyDone')}</Text>
                    <Table>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>{t('scenario.slotLabel')}</Table.Th>
                                <Table.Th>{t('scenario.applyUser')}</Table.Th>
                                <Table.Th>{t('scenario.applyStatus')}</Table.Th>
                                <Table.Th>{t('scenario.applyRowCount')}</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {result.results.map((r) => {
                                const slot = scenario.slots.find((s) => s.id === r.slotID);
                                return (
                                    <Table.Tr key={`${r.slotID}-${r.userID}`}>
                                        <Table.Td>{slot?.label ?? r.slotID}</Table.Td>
                                        <Table.Td>{r.userID}</Table.Td>
                                        <Table.Td>
                                            {r.success ? (
                                                <Badge color="green">OK</Badge>
                                            ) : (
                                                <Stack gap={4}>
                                                    <Badge color="red" w="fit-content">
                                                        {t('scenario.applyFailed')}
                                                    </Badge>
                                                    {r.error && (
                                                        <Text size="xs" c="red" style={{ whiteSpace: 'pre-wrap' }}>
                                                            {r.error}
                                                        </Text>
                                                    )}
                                                </Stack>
                                            )}
                                        </Table.Td>
                                        <Table.Td>{r.insertedRowCount}</Table.Td>
                                    </Table.Tr>
                                );
                            })}
                        </Table.Tbody>
                    </Table>
                    <Group justify="flex-end">
                        <Button onClick={handleClose}>{t('button.close')}</Button>
                    </Group>
                </Stack>
            )}
        </Modal>
    );
};

export default ApplyScenarioModal;
